import './AboutPage.css';

const AboutPage = () => {
  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="container">
          <span className="about-hero-badge">Our Story</span>
          <h1 className="about-hero-title">
            Because the Right Career Decision Begins with the <span className="highlight">Right Information</span>
          </h1>
          <p className="about-hero-subtitle">
            Built by education professionals with over a decade of experience, 12thPlus.com focuses on clarity, planning, and realistic outcomes—rather than guesswork or generic advice.
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="about-stats-section">
        <div className="container">
          <div className="about-stats-grid">
            <div className="about-stat">
              <span className="about-stat-number">10+</span>
              <span className="about-stat-label">Years Experience</span>
            </div>
            <div className="about-stat">
              <span className="about-stat-number">50,000+</span>
              <span className="about-stat-label">Students Guided</span>
            </div>
            <div className="about-stat">
              <span className="about-stat-number">500+</span>
              <span className="about-stat-label">Partner Institutions</span>
            </div>
            <div className="about-stat">
              <span className="about-stat-number">100%</span>
              <span className="about-stat-label">Unbiased Information</span>
            </div>
          </div>
        </div>
      </section>

      {/* Why 12thPlus Exists Section */}
      <section className="section about-why-section">
        <div className="container">
          <div className="about-why-grid">
            <div className="about-why-content">
              <h2>Why <span className="highlight">12thPlus.com</span> Exists</h2>
              <p>
                Today's students face a complex landscape of courses, entrance exams, counselling processes, application timelines, colleges, fees, quotas, and career outcomes.
              </p>
              <p>
                Career guidance in most schools begins after results, when choices are already limited and decisions become rushed. Students end up relying on rumors, peer pressure, or generic advice that doesn't account for their unique situations.
              </p>
              <p className="highlight-text">
                <strong>12thPlus.com was built to make guidance proactive, not reactive.</strong>
              </p>
              <p>
                We bring all this information together into one structured, easy-to-understand platform—so students can make informed decisions without confusion or last-minute pressure.
              </p>
            </div>
            <div className="about-why-cards">
              <div className="why-card">
                <div className="why-card-icon teal">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                </div>
                <h4>Explore Options Early</h4>
              </div>
              <div className="why-card">
                <div className="why-card-icon blue">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 6v6l4 2" />
                  </svg>
                </div>
                <h4>Understand Outcomes</h4>
              </div>
              <div className="why-card">
                <div className="why-card-icon orange">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                </div>
                <h4>Plan in Advance</h4>
              </div>
              <div className="why-card">
                <div className="why-card-icon green">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="2" y1="12" x2="22" y2="12" />
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                  </svg>
                </div>
                <h4>Compare Objectively</h4>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Journey Section */}
      <section className="section about-journey-section">
        <div className="container">
          <h2 className="section-title-center">Our <span className="highlight">Journey</span></h2>
          <p className="section-subtitle">From recognizing a gap to building a solution that serves thousands.</p>

          <div className="journey-timeline">
            <div className="timeline-line"></div>

            <div className="timeline-item">
              <div className="timeline-dot"></div>
              <div className="timeline-card">
                <span className="timeline-badge">The Beginning</span>
                <h3>Recognizing the Gap</h3>
                <p>After years in education counselling, our founders saw the same patterns: students making rushed decisions, families confused by information overload, and guidance arriving too late.</p>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-dot"></div>
              <div className="timeline-card">
                <span className="timeline-badge">The Vision</span>
                <h3>Building 12thPlus</h3>
                <p>We envisioned a platform that brings together everything a Class 11-12 student needs—careers, courses, colleges, exams, finances—in one structured, accessible place.</p>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-dot"></div>
              <div className="timeline-card">
                <span className="timeline-badge">Today</span>
                <h3>Empowering Students</h3>
                <p>Today, 12thPlus.com serves thousands of students and institutions, helping them navigate the complex post-Class 12 landscape with clarity and confidence.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Commitment Section */}
      <section className="section about-commitment-section">
        <div className="container">
          <h2 className="section-title-center">Our Commitment to <span className="highlight">Students</span></h2>
          <p className="section-subtitle">Every decision we make is guided by these core principles.</p>

          <div className="commitment-grid">
            <div className="commitment-card">
              <div className="commitment-icon orange">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3>Clarity over Confusion</h3>
              <p>We cut through the noise to deliver straightforward, actionable information that students and parents can understand and use.</p>
            </div>

            <div className="commitment-card">
              <div className="commitment-icon blue">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
              </div>
              <h3>Planning over Panic</h3>
              <p>We believe in proactive guidance, not reactive scrambling. Start early, decide confidently.</p>
            </div>

            <div className="commitment-card">
              <div className="commitment-icon pink">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
              </div>
              <h3>Guidance over Pressure</h3>
              <p>We inform and empower—never push. The right path is the one you choose for yourself.</p>
            </div>

            <div className="commitment-card">
              <div className="commitment-icon teal">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                </svg>
              </div>
              <h3>Ethics over Profit</h3>
              <p>No paid rankings, no hidden agendas. Just honest, unbiased information that puts students first.</p>
            </div>
          </div>
        </div>
      </section>

      {/* What Makes Us Different Section */}
      <section className="about-different-section">
        <div className="container">
          <h2 className="section-title-white">What Makes Us <span className="highlight-light">Different</span></h2>
          <p className="section-subtitle-light">In a world of paid promotions and biased rankings, we stand apart.</p>

          <div className="different-grid">
            <div className="different-card">
              <div className="different-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <line x1="9" y1="9" x2="15" y2="15" />
                  <line x1="15" y1="9" x2="9" y2="15" />
                </svg>
              </div>
              <div className="different-content">
                <h3>No Advertisements</h3>
                <p>We don't run ads or promote colleges for money. Our recommendations are based on facts, not payments.</p>
              </div>
            </div>

            <div className="different-card">
              <div className="different-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
              </div>
              <div className="different-content">
                <h3>No Paid Rankings</h3>
                <p>Every college listing is neutral. We don't accept money to feature or rank institutions higher.</p>
              </div>
            </div>

            <div className="different-card">
              <div className="different-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              <div className="different-content">
                <h3>Verified Information</h3>
                <p>Our team verifies data from official sources. We update constantly to ensure accuracy.</p>
              </div>
            </div>

            <div className="different-card">
              <div className="different-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
              </div>
              <div className="different-content">
                <h3>Student-First Approach</h3>
                <p>Every feature, every piece of content is designed with the student's best interest in mind.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Powered by GooCampus Section */}
      <section className="section about-powered-section">
        <div className="container">
          <div className="about-powered-badge">
            <span>Powered by</span>
            <strong>GooCampus</strong>
          </div>
          <h2 className="section-title-center">Built on a Decade of <span className="highlight">Experience</span></h2>
          <p className="powered-description">
            12thPlus.com is developed by the team behind GooCampus, with over a decade of experience in student counselling and career guidance. We've helped thousands of students find their path, and now we're bringing that expertise to every Class 11 and 12 student in India.
          </p>
          <div className="about-powered-badges">
            <div className="about-powered-badge-item">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              <span>Expert Team</span>
            </div>
            <div className="about-powered-badge-item">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              <span>Trusted by Thousands</span>
            </div>
            <div className="about-powered-badge-item">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                <polyline points="17 6 23 6 23 12" />
              </svg>
              <span>Proven Track Record</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="about-cta-section">
        <div className="container">
          <h2>Ready to Take Control of Your <span className="highlight-light">Future</span>?</h2>
          <p>Whether you're a student looking for guidance or an institution seeking to support your students better, we're here to help.</p>
          <div className="about-cta-buttons">
            <a href="https://12thplus.com/app/login" className="btn btn-white">
              Explore for Students
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </a>
            <a href="https://12thplus.com/app/login" className="btn btn-teal">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
              For Institutions
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
